import { ISortByQuery } from '@common/api.models';
import { createSearchAsyncFn } from '@common/search.services';
import { envConfigs } from '@config/envConfigs';
import { createDownloadFileAsyncFn, createDownloadFileWithCustomPropsAsyncFn } from '@file/file.services';
import { IFileResponse, IPdfFileDownloadRequest } from '@file/projectFile/projectFile.models';
import { IItemResponse, IPagedResultsV1 } from '@gcl/react-core';
import { apiServerDelete, apiServerGet, apiServerPost, apiServerPut } from '@common/axios.services';
import { IDownloadFileBaseProps } from '@file/file.models';
import {
  ICreateProjectLoadingCertRequest,
  IProjectLoadingRemovalCert,
  IProjectLoadingRemovalCertCheckList,
  IProjectLoadingRemovalCertDrawing,
  IProjectLoadingRemovalCertT3,
  IProjectLoadingRemovalIceCertRequestProps,
  IUpdateProjectLoadingRemovalCertRequest,
} from './detail/permitToLoadRemoveDetailPage.models';
import { ILoadingRemovalCertSearch, ILoadingRemovalCertSearchFilter } from './permitToLoadRemove.models';

export const apiRoutes = {
  loadingRemovalCertLoading: '/LoadingRemovalCerts/Loading',
  loadingRemovalCert: '/LoadingRemovalCerts',
  loadingRemovalCertWithId: (id: number) => `/LoadingRemovalCerts/${id}`,
  loadingRemovalCertDetail: (id: number) => `/LoadingRemovalCerts/${id}/Details`,
  loadingRemovalCertRevisions: (id: number) => `/LoadingRemovalCerts/${id}/Revisions`,
  loadingRemovalCertRemovalCerts: (id: number) => `/LoadingRemovalCerts/${id}/RemovalCerts`,
  loadingRemovalCertIceCertRequest: (id: number) => `/LoadingRemovalCerts/${id}/IceCertRequest`,
  loadingRemovalCertCreateAndIssueLoadCert: '/LoadingRemovalCerts/CreateAndIssueLoadCert',
  loadingRemovalCertSrpIssued: (id: number) => `/LoadingRemovalCerts/${id}/SrpIssued`,
  loadingRemovalCertTwcApproved: (id: number) => `/LoadingRemovalCerts/${id}/TwcApproved`,
  loadingRemovalCertIceApproved: (id: number) => `/LoadingRemovalCerts/${id}/IceApproved`,
  loadingRemovalCertDrawings: '/LoadingRemovalCerts/Drawings',
  loadingRemovalCertT3: '/LoadingRemovalCerts/T3',
  loadingRemovalCertCheckList: (id: number) => `/LoadingRemovalCerts/${id}/CheckLists`,
  loadingRemovalCertSearch: '/LoadingRemovalCerts/Search',
  exportLoadingRemovalCertSearchResult: '/LoadingRemovalCerts/ExportExcel',
  exportLoadingRemovalCertSearchResultPdf: '/LoadingRemovalCerts/ExportPdf',
  downloadLoadingRemovalCertPdf: (id: number) => `/LoadingRemovalCerts/${id}/Pdf`,
  downloadLoadingRemovalCertProjectDiscussionsPdf: (id: number) => `/LoadingRemovalCerts/${id}/ProjectDiscussions/Pdf`,
  downloadLoadingRemovalCertIceConstructionCertPdf: (id: number) =>
    `/LoadingRemovalCerts/${id}/IceConstructionCert/Pdf`,
  downloadLoadingRemovalCertIceRemovalCertPdf: (id: number) => `/LoadingRemovalCerts/${id}/IceRemovalCert/Pdf`,
};

export const searchLoadingRemovalCertAsync = createSearchAsyncFn<
  ILoadingRemovalCertSearchFilter,
  ILoadingRemovalCertSearch
>(apiRoutes.loadingRemovalCertSearch);

export interface IExportProjectLoadingRemovalCertProps extends IDownloadFileBaseProps {
  sortByQuery?: ISortByQuery;
  searchFilter: ILoadingRemovalCertSearchFilter;
}

export const exportProjectLoadingRemovalCertSearchExcelAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportProjectLoadingRemovalCertProps>(
    apiRoutes.exportLoadingRemovalCertSearchResult,
  );

export const exportProjectLoadingRemovalCertSearchPdfAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportProjectLoadingRemovalCertProps>(
    apiRoutes.exportLoadingRemovalCertSearchResultPdf,
  );

export const postLoadingRemovalCertAsync = async (
  request: ICreateProjectLoadingCertRequest,
): Promise<IItemResponse<IProjectLoadingRemovalCert>> => {
  const result = await apiServerPost<IItemResponse<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCert}`,
    request,
  );
  return result.data;
};

export const postLoadingRemovalCertCreateAndIssueAsync = async (
  request: ICreateProjectLoadingCertRequest,
): Promise<IItemResponse<IProjectLoadingRemovalCert>> => {
  const result = await apiServerPost<IItemResponse<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertCreateAndIssueLoadCert}`,
    request,
  );
  return result.data;
};

export const getLoadingRemovalCertAsync = async (id: number): Promise<IItemResponse<IProjectLoadingRemovalCert>> => {
  const result = await apiServerGet<IItemResponse<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertDetail(id)}`,
  );
  return result.data;
};

export const putLoadingRemovalCertAsync = async (
  id: number,
  props: IUpdateProjectLoadingRemovalCertRequest,
): Promise<IItemResponse<IProjectLoadingRemovalCert>> => {
  const result = await apiServerPut<IItemResponse<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertWithId(id)}`,
    props,
  );
  return result.data;
};

export const deleteLoadingRemovalCertAsync = async (id: number): Promise<boolean> => {
  const result = await apiServerDelete<boolean>(`${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertWithId(id)}`);
  return result.data;
};

export const postLoadingRemovalCertRevisionsAsync = async (
  id: number,
): Promise<IItemResponse<IProjectLoadingRemovalCert>> => {
  const result = await apiServerPost<IItemResponse<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertRevisions(id)}`,
  );
  return result.data;
};

export const getLoadingRemovalCertRevisionsAsync = async (
  id: number,
): Promise<IPagedResultsV1<IProjectLoadingRemovalCert>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertRevisions(id)}`,
  );
  return result.data;
};

export const postLoadingRemovalCertRemovalCertsAsync = async (
  id: number,
): Promise<IItemResponse<IProjectLoadingRemovalCert>> => {
  const result = await apiServerPost<IItemResponse<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertRemovalCerts(id)}`,
  );
  return result.data;
};
export const getLoadingRemovalCertRemovalCertsAsync = async (
  id: number,
): Promise<IPagedResultsV1<IProjectLoadingRemovalCert>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertRemovalCerts(id)}`,
  );
  return result.data;
};

export const putLoadingRemovalCertIceCertRequestAsync = async (
  id: number,
  props: IProjectLoadingRemovalIceCertRequestProps,
): Promise<boolean> => {
  const result = await apiServerPut<boolean>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertIceCertRequest(id)}`,
    {},
    { params: props },
  );
  return result.data;
};

export const putLoadingRemovalCertSrpIssuedAsync = async (
  id: number,
  props: IUpdateProjectLoadingRemovalCertRequest,
): Promise<IItemResponse<IProjectLoadingRemovalCert>> => {
  const result = await apiServerPut<IItemResponse<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertSrpIssued(id)}`,
    props,
  );
  return result.data;
};

export const putLoadingRemovalCertTwcApprovedAsync = async (
  id: number,
  props: IUpdateProjectLoadingRemovalCertRequest,
): Promise<IItemResponse<IProjectLoadingRemovalCert>> => {
  const result = await apiServerPut<IItemResponse<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertTwcApproved(id)}`,
    props,
  );
  return result.data;
};

export const putLoadingRemovalCertIceApprovedAsync = async (
  id: number,
  props: IUpdateProjectLoadingRemovalCertRequest,
): Promise<IItemResponse<IProjectLoadingRemovalCert>> => {
  const result = await apiServerPut<IItemResponse<IProjectLoadingRemovalCert>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertIceApproved(id)}`,
    props,
  );
  return result.data;
};

export const getLoadingRemovalCertDrawingsAsync = async (
  loadingRemovalCertId: number,
): Promise<IPagedResultsV1<IProjectLoadingRemovalCertDrawing>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectLoadingRemovalCertDrawing>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertDrawings}`,
    { params: { loadingRemovalCertId } },
  );
  return result.data;
};

export const getLoadingRemovalCertCheckListAsync = async (
  id: number,
  projectId: number,
): Promise<IPagedResultsV1<IProjectLoadingRemovalCertCheckList>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectLoadingRemovalCertCheckList>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertCheckList(id)}`,
    {
      params: { projectId, loadingRemovalCertId: id },
    },
  );
  return result.data;
};

export const getLoadingRemovalCertT3Async = async (
  loadingRemovalCertId: number,
): Promise<IPagedResultsV1<IProjectLoadingRemovalCertT3>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectLoadingRemovalCertT3>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.loadingRemovalCertT3}`,
    { params: { loadingRemovalCertId } },
  );
  return result.data;
};

export const downloadLoadingRemovalCertPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadLoadingRemovalCertPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};

export const downloadLoadingRemovalCertProjectDiscussionsPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadLoadingRemovalCertProjectDiscussionsPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};

export const downloadLoadingRemovalCertIceConstructionCertPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadLoadingRemovalCertIceConstructionCertPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};

export const downloadLoadingRemovalCertIceRemovalCertPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadLoadingRemovalCertIceRemovalCertPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};
